import { isReservedPreviewQueryParam } from '@Pages/utils/previewQueryParams';
import { useCallback, useMemo, useSyncExternalStore } from 'react';

export type QueryParamSchemaEntry =
  | { type?: 'string'; default?: string }
  | { type: 'number'; default?: number }
  | { type: 'boolean'; default?: boolean }
  | { type: 'array'; default?: readonly string[] };

export type QueryParamSchema = Record<string, QueryParamSchemaEntry>;
export type QueryParams = Record<string, string | string[]>;

function isReservedQueryParam(key: string): boolean {
  return (
    key === '_diagonal_embed' ||
    key.startsWith('_diagonal_embed[') ||
    isReservedPreviewQueryParam(key)
  );
}

type QueryParamValue<TEntry extends QueryParamSchemaEntry> = TEntry extends { type: 'number' }
  ? number
  : TEntry extends { type: 'boolean' }
    ? boolean
    : TEntry extends { type: 'array' }
      ? string[]
      : string;

export type QueryParamsFromSchema<TSchema extends QueryParamSchema> = {
  [TKey in keyof TSchema]: TSchema[TKey] extends { default: unknown }
    ? QueryParamValue<TSchema[TKey]>
    : QueryParamValue<TSchema[TKey]> | undefined;
};

type DefinedQueryParamSetValue<TValue> = TValue extends number
  ? number | null | undefined
  : TValue extends boolean
    ? boolean | null | undefined
    : TValue extends readonly string[]
      ? readonly string[] | null | undefined
      : string | null | undefined;

type QueryParamSetValue<TValue> = DefinedQueryParamSetValue<NonNullable<TValue>>;

export type QueryParamsPatch<TParams extends Record<string, unknown>> = Partial<{
  [TKey in keyof TParams]: QueryParamSetValue<TParams[TKey]>;
}>;

export type SetQueryParams<TParams extends Record<string, unknown>> = (
  next: QueryParamsPatch<TParams> | ((current: TParams) => QueryParamsPatch<TParams>)
) => void;

const subscribers = new Set<() => void>();

function emitChange(): void {
  subscribers.forEach((subscriber) => subscriber());
}

function subscribe(subscriber: () => void): () => void {
  subscribers.add(subscriber);

  if (subscribers.size === 1 && typeof window !== 'undefined') {
    window.addEventListener('popstate', emitChange);
    document.addEventListener('inertia:navigate', emitChange);
  }

  return () => {
    subscribers.delete(subscriber);

    if (subscribers.size === 0 && typeof window !== 'undefined') {
      window.removeEventListener('popstate', emitChange);
      document.removeEventListener('inertia:navigate', emitChange);
    }
  };
}

/**
 * Calls the listener after page code writes query params, and after a navigation or history
 * change. A query param write uses `history.replaceState`, which no browser event reports.
 */
export function subscribeToQueryParamChanges(listener: () => void): () => void {
  return subscribe(listener);
}

function getSearch(): string {
  return typeof window === 'undefined' ? '' : window.location.search;
}

function parseBoolean(value: string | null): boolean | undefined {
  if (value === null) {
    return undefined;
  }

  const normalized = value.toLowerCase();
  if (normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'on') {
    return true;
  }

  if (normalized === 'false' || normalized === '0' || normalized === 'no' || normalized === 'off') {
    return false;
  }

  return undefined;
}

function parseWithoutSchema(searchParams: URLSearchParams): QueryParams {
  const result: QueryParams = {};

  for (const key of new Set(searchParams.keys())) {
    if (isReservedQueryParam(key)) {
      continue;
    }

    const values = searchParams.getAll(key);
    result[key] = values.length === 1 ? values[0] : values;
  }

  return result;
}

function assertSchemaKeysAreAvailable(schema: QueryParamSchema): void {
  const reservedKey = Object.keys(schema).find(isReservedQueryParam);
  if (reservedKey) {
    throw new Error(`Query parameter '${reservedKey}' is reserved for page rendering.`);
  }
}

function schemaKey(schema: QueryParamSchema | undefined): string {
  if (!schema) {
    return '';
  }

  assertSchemaKeysAreAvailable(schema);

  return JSON.stringify(
    Object.keys(schema)
      .toSorted((a, b) => a.localeCompare(b))
      .map((key) => {
        const entry = schema[key];
        const type = entry.type ?? 'string';
        return entry.default === undefined ? [key, type] : [key, type, entry.default];
      })
  );
}

function schemaFromKey(key: string): QueryParamSchema | undefined {
  if (key === '') {
    return undefined;
  }

  const entries = JSON.parse(key) as Array<
    [string, NonNullable<QueryParamSchemaEntry['type']>, (string | number | boolean | string[])?]
  >;

  return Object.fromEntries(
    entries.map(([name, type, defaultValue]) => [
      name,
      defaultValue === undefined ? { type } : { type, default: defaultValue }
    ])
  ) as QueryParamSchema;
}

function parseWithSchema<TSchema extends QueryParamSchema>(
  searchParams: URLSearchParams,
  schema: TSchema
): QueryParamsFromSchema<TSchema> {
  assertSchemaKeysAreAvailable(schema);
  const result: Record<string, string | number | boolean | string[] | undefined> = {};

  for (const [key, entry] of Object.entries(schema)) {
    if (entry.type === 'array') {
      const values = searchParams.getAll(key);
      result[key] = values.length > 0 ? values : entry.default ? [...entry.default] : undefined;
      continue;
    }

    const rawValue = searchParams.get(key);
    if (entry.type === 'number') {
      const parsed = rawValue === null || rawValue.trim() === '' ? Number.NaN : Number(rawValue);
      result[key] = Number.isFinite(parsed) ? parsed : entry.default;
      continue;
    }

    if (entry.type === 'boolean') {
      result[key] = parseBoolean(rawValue) ?? entry.default;
      continue;
    }

    result[key] = rawValue ?? entry.default;
  }

  return result as QueryParamsFromSchema<TSchema>;
}

function parseParams(
  search: string,
  schema: QueryParamSchema | undefined
): Record<string, unknown> {
  const searchParams = new URLSearchParams(search);
  return schema ? parseWithSchema(searchParams, schema) : parseWithoutSchema(searchParams);
}

function writeValue(searchParams: URLSearchParams, key: string, value: unknown): void {
  searchParams.delete(key);

  if (value === null || value === undefined) {
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item) => searchParams.append(key, String(item)));
    return;
  }

  searchParams.set(key, String(value));
}

/**
 * Replaces query parameter values in the current history entry. `null` and `undefined`
 * remove a parameter. Reserved page-rendering parameters are rejected.
 */
export function replaceQueryParams(
  patch: Record<string, unknown>,
  schema?: QueryParamSchema
): void {
  if (typeof window === 'undefined') {
    return;
  }

  const url = new URL(window.location.href);

  for (const [key, value] of Object.entries(patch)) {
    if (isReservedQueryParam(key)) {
      throw new Error(`Query parameter '${key}' is reserved for page rendering.`);
    }

    if (schema && !(key in schema)) {
      throw new Error(`Query parameter '${key}' is not defined in the schema.`);
    }

    writeValue(url.searchParams, key, value);
  }

  const nextUrl = `${url.pathname}${url.search}${url.hash}`;
  const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (nextUrl === currentUrl) {
    return;
  }

  window.history.replaceState(window.history.state, '', nextUrl);
  emitChange();
}

export function useQueryParams(): [QueryParams, SetQueryParams<QueryParams>];
export function useQueryParams<const TSchema extends QueryParamSchema>(
  schema: TSchema
): [QueryParamsFromSchema<TSchema>, SetQueryParams<QueryParamsFromSchema<TSchema>>];
export function useQueryParams<TSchema extends QueryParamSchema | undefined>(schema?: TSchema) {
  const search = useSyncExternalStore(subscribe, getSearch, getSearch);
  const currentSchemaKey = schemaKey(schema);
  const params = useMemo(
    () => parseParams(search, schemaFromKey(currentSchemaKey)),
    [search, currentSchemaKey]
  );

  const setParams = useCallback(
    (
      next:
        | QueryParamsPatch<typeof params>
        | ((current: typeof params) => QueryParamsPatch<typeof params>)
    ) => {
      const liveSchema = schemaFromKey(currentSchemaKey);
      const patch = typeof next === 'function' ? next(parseParams(getSearch(), liveSchema)) : next;
      replaceQueryParams(patch as Record<string, unknown>, liveSchema);
    },
    [currentSchemaKey]
  );

  return [params, setParams] as const;
}
